import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import Modal from '../../../components/v2/common/Modal/Modal';
import UsersService from '../../../services/UsersService';

interface SpoofVendorModalProps {
  open: boolean;
  onClose: () => void;
  vendorId: string;
}

interface VendorUser {
  id_member: string;
  full_name: string;
  email: string;
}

export default function SpoofVendorModal({ open, onClose, vendorId }: SpoofVendorModalProps) {
  const [vendorUsers, setVendorUsers] = useState<Array<VendorUser>>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [spoofUrl, setSpoofUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadVendorUsers();
    } else {
      flushSpoofingState();
    }
  }, [open]);

  const flushSpoofingState = () => {
    setSpoofUrl(null);
    setSelectedUserId('');
    setError(null);
  };

  const loadVendorUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await UsersService.getVendorUsers(vendorId);
      setVendorUsers(response.result || []);
    } catch (err) {
      setError('Failed to load vendor users.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateSpoofLink = async () => {
    if (!selectedUserId) {
      setError('Please select a user to spoof');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await UsersService.spoofUserTokenOnly(selectedUserId);

      // Construct the extranet URL
      const url = `${window.configs.EXTRANET_PORTAL}/redirect?token=${token}&url=${window.configs.EXTRANET_PORTAL}?utm_source=admin&utm_medium=spoof&utm_campaign=vendor_spoof`;

      setSpoofUrl(url);

      // Auto-copy to clipboard
      navigator.clipboard.writeText(url);
    } catch (err) {
      setError('Failed to generate spoof token.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeUser = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedUserId(event.target.value);
  };

  const handleClose = () => {
    flushSpoofingState();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Spoof Extranet User">
      <Box sx={{ minWidth: 400, minHeight: 300, p: 1 }}>
        {isLoading && !spoofUrl ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!spoofUrl ? (
              <>
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                  <FormLabel component="legend">Select Vendor User</FormLabel>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2, pr: 1 }}>
                    <RadioGroup value={selectedUserId} onChange={handleChangeUser} sx={{ width: '100%' }}>
                      {vendorUsers.map((user) => (
                        <Box
                          key={user.id_member}
                          sx={{
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                          }}
                        >
                          <Radio
                            value={user.id_member}
                            checked={selectedUserId === user.id_member}
                            onChange={handleChangeUser}
                            sx={{ verticalAlign: 'top' }}
                          />
                          <Box sx={{ display: 'inline-block', ml: 1 }}>
                            <Typography variant="body1">{user.full_name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </RadioGroup>
                  </Box>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={generateSpoofLink}
                  disabled={!selectedUserId || isLoading}
                  fullWidth
                >
                  Generate Spoof Link
                </Button>
              </>
            ) : (
              <Stack direction="column" spacing={2}>
                <Alert severity="warning">Extreme caution, paste into incognito only</Alert>

                <Alert severity="info">Your link has been copied to the clipboard!</Alert>

                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Spoofing link for vendor extranet"
                  value={spoofUrl}
                  fullWidth
                  multiline
                  disabled
                />
              </Stack>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
}
